import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { routes } from '@routes';
import { Icon, IconNames } from '@components/icon';
import { Button } from '@components/button';
import { Link } from '@components/link';
import { MobileSearchModal } from '@components/mobile-search-modal';
import { ListingMapCard } from './listing-map-card';
import { FeaturesModal } from './features-modal';
import { SearchMobileBar } from './search-mobile-bar';
import { SearchResultsList } from './search-results-list';
import { SearchMapView } from './search-map-view';
import { SearchToggleButton } from './search-toggle-button';
import { useSearchMapState } from './use-search-map-state';
export const SearchMap: React.FC = () => {
  const $ = _c(86);
  const state = useSearchMapState();
  const {
    search: t0,
    map,
    resultsContainerRef,
    isListActive,
    shouldShowContent,
    shouldShowLoadingState,
    hoveredListingSlug,
    setHoveredListingSlug,
    setSelectedListingId,
    selectedListing,
    updateSelectedListing,
    isAboveMinZoom,
    zoomingOutToNewListing,
    setZoomingOutToNewListing,
    mapListings,
    placeName,
    searchedCountry,
    hasFiltersApplied,
    isValidationError,
    listingsError,
    retrySearch,
    isGeoResolutionError,
    saveScrollPosition,
    clearDates,
    resetFilters,
    onMapCardClose,
    onMapClick,
    debouncedMapChangeHandler,
    onMapDragEnd,
    onMapZoomChanged,
    toggleListView
  } = state;
  const {
    activeFilters,
    adultCount,
    checkInDate,
    checkOutDate,
    childCount,
    geoSearchBox,
    infantCount,
    mapZoom,
    petCount,
    placeId,
    resetPagination,
    setActiveFilters,
    setMinNumberOfBathrooms,
    setMinNumberOfBeds,
    setMinNumberOfRooms,
    setMinPrice,
    setMaxPrice,
    setPropertyTypes
  } = t0;
  if (isGeoResolutionError) {
    let t1;
    let t2;
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex items-center justify-center w-16 h-16 rounded-full bg-pool mb-5"><FontAwesomeIcon icon={faLocationDot} className="text-primary-main text-2xl" /></div>;
      t2 = <h2 className="text-xl font-semibold text-primary-main mb-2">Unable to load this location</h2>;
      t3 = <p className="text-sm text-primary-main/70 mb-8 leading-relaxed">We couldn't resolve the map area for your search. This can happen if the location is no longer available. Please try again or start a new search.</p>;
      $[0] = t1;
      $[1] = t2;
      $[2] = t3;
    } else {
      t1 = $[0];
      t2 = $[1];
      t3 = $[2];
    }
    let t4;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex w-full h-full justify-center pt-24 sm:pt-32"><div className="flex flex-col items-center text-center px-6 max-w-md">{t1}{t2}{t3}<div className="flex flex-col sm:flex-row items-center gap-3"><Link to={routes.home} isButton={true} variant="outlined" size="md">Back to home</Link><Button size="md" onClick={_temp}>Try again</Button></div></div></div>;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    return t4;
  }
  if (isValidationError) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div>Something went wrong</div>;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  if (listingsError && !state.search.listingsLoading) {
    let t1;
    let t2;
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-pool/50"><Icon name={IconNames.TRIANGLE_EXCLAMATION} className="text-primary-main text-xl" /></div>;
      t2 = <h2 className="text-2xl font-medium text-primary-main">Search results unavailable</h2>;
      t3 = <p className="text-primary-main/60">We couldn't load properties for this area. This is usually temporary — please try again.</p>;
      $[5] = t1;
      $[6] = t2;
      $[7] = t3;
    } else {
      t1 = $[5];
      t2 = $[6];
      t3 = $[7];
    }
    let t4;
    if ($[8] !== retrySearch) {
      t4 = <div className="flex w-full h-full min-h-[50vh] items-center justify-center"><div className="flex flex-col items-center text-center space-y-5 px-6 max-w-md">{t1}{t2}{t3}<Button onClick={retrySearch}>Try again</Button></div></div>;
      $[8] = retrySearch;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t1;
  if ($[10] !== resetPagination || $[11] !== setActiveFilters || $[12] !== setMaxPrice || $[13] !== setMinNumberOfBathrooms || $[14] !== setMinNumberOfBeds || $[15] !== setMinNumberOfRooms || $[16] !== setMinPrice || $[17] !== setPropertyTypes) {
    t1 = <FeaturesModal viewAllProperties={(propertyTypes, minNumberOfRooms, minNumberOfBathrooms, minPrice, maxPrice, minNumberOfBeds, filters) => {
      setMinNumberOfBathrooms(minNumberOfBathrooms);
      setMinNumberOfRooms(minNumberOfRooms);
      setMinNumberOfBeds(minNumberOfBeds);
      setPropertyTypes(propertyTypes);
      setMinPrice(minPrice);
      setMaxPrice(maxPrice);
      setActiveFilters(filters);
      resetPagination();
    }} />;
    $[10] = resetPagination;
    $[11] = setActiveFilters;
    $[12] = setMaxPrice;
    $[13] = setMinNumberOfBathrooms;
    $[14] = setMinNumberOfBeds;
    $[15] = setMinNumberOfRooms;
    $[16] = setMinPrice;
    $[17] = setPropertyTypes;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  const t2 = placeName || undefined;
  const t3 = placeId || undefined;
  let t4;
  if ($[19] !== activeFilters || $[20] !== adultCount || $[21] !== checkInDate || $[22] !== checkOutDate || $[23] !== childCount || $[24] !== geoSearchBox || $[25] !== infantCount || $[26] !== mapZoom || $[27] !== petCount || $[28] !== t2 || $[29] !== t3) {
    t4 = <MobileSearchModal placeName={t2} placeId={t3} checkInDate={checkInDate} checkOutDate={checkOutDate} adultCount={adultCount} childCount={childCount} infantCount={infantCount} petCount={petCount} mapZoom={mapZoom} geoSearchBox={geoSearchBox} activeFilters={activeFilters} />;
    $[19] = activeFilters;
    $[20] = adultCount;
    $[21] = checkInDate;
    $[22] = checkOutDate;
    $[23] = childCount;
    $[24] = geoSearchBox;
    $[25] = infantCount;
    $[26] = mapZoom;
    $[27] = petCount;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[31] !== placeName) {
    t5 = <SearchMobileBar placeName={placeName} />;
    $[31] = placeName;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== setZoomingOutToNewListing) {
    t6 = () => setZoomingOutToNewListing(true);
    $[33] = setZoomingOutToNewListing;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== clearDates || $[36] !== hasFiltersApplied || $[37] !== isAboveMinZoom || $[38] !== isListActive || $[39] !== resetFilters || $[40] !== resultsContainerRef || $[41] !== saveScrollPosition || $[42] !== searchedCountry || $[43] !== selectedListing || $[44] !== setHoveredListingSlug || $[45] !== shouldShowContent || $[46] !== shouldShowLoadingState || $[47] !== t6 || $[48] !== updateSelectedListing || $[49] !== zoomingOutToNewListing) {
    t7 = <SearchResultsList resultsContainerRef={resultsContainerRef} isListActive={isListActive} shouldShowContent={shouldShowContent} shouldShowLoadingState={shouldShowLoadingState} searchedCountry={searchedCountry} hasFiltersApplied={hasFiltersApplied} isAboveMinZoom={isAboveMinZoom} zoomingOutToNewListing={zoomingOutToNewListing} selectedListing={selectedListing} onHoverListing={setHoveredListingSlug} onSaveScrollPosition={saveScrollPosition} onClearDates={clearDates} onResetFilters={resetFilters} onZoomOut={t6} onFavoritedSuccess={updateSelectedListing} />;
    $[35] = clearDates;
    $[36] = hasFiltersApplied;
    $[37] = isAboveMinZoom;
    $[38] = isListActive;
    $[39] = resetFilters;
    $[40] = resultsContainerRef;
    $[41] = saveScrollPosition;
    $[42] = searchedCountry;
    $[43] = selectedListing;
    $[44] = setHoveredListingSlug;
    $[45] = shouldShowContent;
    $[46] = shouldShowLoadingState;
    $[47] = t6;
    $[48] = updateSelectedListing;
    $[49] = zoomingOutToNewListing;
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  let t8;
  if ($[51] !== debouncedMapChangeHandler || $[52] !== hoveredListingSlug || $[53] !== isListActive || $[54] !== map || $[55] !== mapListings || $[56] !== onMapCardClose || $[57] !== onMapClick || $[58] !== onMapDragEnd || $[59] !== onMapZoomChanged || $[60] !== selectedListing || $[61] !== setSelectedListingId || $[62] !== updateSelectedListing) {
    t8 = <SearchMapView isListActive={isListActive} map={map} mapListings={mapListings} hoveredListingSlug={hoveredListingSlug} selectedListing={selectedListing} onMapClick={onMapClick} onDragEnd={onMapDragEnd} onZoomChanged={onMapZoomChanged} onResize={debouncedMapChangeHandler} onMapCardClose={onMapCardClose} onSelectListing={setSelectedListingId} onUpdateFavorite={updateSelectedListing} />;
    $[51] = debouncedMapChangeHandler;
    $[52] = hoveredListingSlug;
    $[53] = isListActive;
    $[54] = map;
    $[55] = mapListings;
    $[56] = onMapCardClose;
    $[57] = onMapClick;
    $[58] = onMapDragEnd;
    $[59] = onMapZoomChanged;
    $[60] = selectedListing;
    $[61] = setSelectedListingId;
    $[62] = updateSelectedListing;
    $[63] = t8;
  } else {
    t8 = $[63];
  }
  let t9;
  if ($[64] !== isListActive || $[65] !== toggleListView) {
    t9 = <SearchToggleButton isListActive={isListActive} onToggle={toggleListView} />;
    $[64] = isListActive;
    $[65] = toggleListView;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  let t10;
  if ($[67] !== adultCount || $[68] !== checkInDate || $[69] !== checkOutDate || $[70] !== childCount || $[71] !== infantCount || $[72] !== isListActive || $[73] !== onMapCardClose || $[74] !== petCount || $[75] !== selectedListing || $[76] !== updateSelectedListing) {
    t10 = selectedListing && selectedListing.slug && !isListActive && <div className="fixed flex max-w-[400px] px-4 aspect-3 right-1/2 bottom-0 -translate-y-24 translate-x-1/2 lg:hidden z-50 w-full min-[400px]:px-0"><ListingMapCard listing={selectedListing} isFavorited={selectedListing.isFavorited} closeMapCard={onMapCardClose} onFavoritedSuccess={isNowFavorited => {
        updateSelectedListing(selectedListing, isNowFavorited);
      }} listingRoute={routes.listings({
        slug: selectedListing.slug,
        adultCount,
        childCount,
        infantCount,
        petCount,
        checkInDate: checkInDate || undefined,
        checkOutDate: checkOutDate || undefined
      })} /></div>;
    $[67] = adultCount;
    $[68] = checkInDate;
    $[69] = checkOutDate;
    $[70] = childCount;
    $[71] = infantCount;
    $[72] = isListActive;
    $[73] = onMapCardClose;
    $[74] = petCount;
    $[75] = selectedListing;
    $[76] = updateSelectedListing;
    $[77] = t10;
  } else {
    t10 = $[77];
  }
  let t11;
  if ($[78] !== t1 || $[79] !== t10 || $[80] !== t4 || $[81] !== t5 || $[82] !== t7 || $[83] !== t8 || $[84] !== t9) {
    t11 = <div className="flex w-full h-full sm:max-h-[calc(100vh-178px)] bg-white">{t1}{t4}{t5}{t7}{t8}{t9}{t10}</div>;
    $[78] = t1;
    $[79] = t10;
    $[80] = t4;
    $[81] = t5;
    $[82] = t7;
    $[83] = t8;
    $[84] = t9;
    $[85] = t11;
  } else {
    t11 = $[85];
  }
  return t11;
};
function _temp() {
  return window.location.reload();
}